import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Handshake, Send, MapPin, Clock, CheckCircle,
  XCircle, MessageCircle, HelpCircle, Paperclip, FileText, Image, X, Download
} from 'lucide-react';
import axios from 'axios';
import io from 'socket.io-client';
import { Joyride } from 'react-joyride';
import { useToast } from '../contexts/ToastContext';
import { isAuthenticated, getAuthHeader, getAssociateData, getToken } from '../services/otpAuthService';
import { sessioneDiDipendente } from '../utils/sezioniDipendente';
import ConfirmDialog from '../components/ConfirmDialog';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_BASE.replace('/api', '');

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'new': { label: 'Nuovo', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  'in-progress': { label: 'In Corso', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  'resolved': { label: 'Risolto', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' },
  'closed': { label: 'Chiuso', color: 'text-gray-700 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-900/30' }
};

const PRACTICE_ICONS: Record<string, string> = {
  'Immatricolazioni e passaggi di proprietà': '🚗',
  'Consulenza per le pratiche agricole e operatrici': '🚜',
  'Trasporto merci': '🚛',
  'Nazionalizzazioni': '🌍',
  'Consulenze tecniche': '🔧',
  'Trasporti eccezionali': '🚚',
  'Servizi nautici (Studio abilitato STED)': '⛵',
  'Trasporto rifiuti': '♻️',
  'Rinnovo patenti (comprese le patenti nautiche)': '🪪'
};

interface Attachment {
  filename: string;
  originalName: string;
  url: string;
  mimetype: string;
  size: number;
}

interface ConversationMsg {
  sender: 'requester' | 'target';
  senderAssociateId: string;
  senderName: string;
  message: string;
  attachments?: Attachment[];
  sentAt: string;
  readAt?: string;
}

interface CollaborationDetail {
  _id: string;
  requesterId: string;
  requesterSnapshot: { nominativo: string; citta: string; provincia: string; email: string; codice: string };
  targetId: string;
  targetSnapshot: { nominativo: string; citta: string; provincia: string; email: string; codice: string };
  practiceType: string;
  status: string;
  conversation: ConversationMsg[];
  createdAt: string;
  updatedAt: string;
}

export default function CollaboraDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [collaboration, setCollaboration] = useState<CollaborationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [currentAssociateId, setCurrentAssociateId] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const socketRef = useRef<any>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guided tour
  const TOUR_CHAT_KEY = 'collabora_tour_chat';
  const [runTourChat, setRunTourChat] = useState(false);
  const [tourChatKey, setTourChatKey] = useState(0);

  const isClosed = collaboration ? ['resolved', 'closed'].includes(collaboration.status) : false;

  const chatTourSteps = [
    {
      target: '#tour-chat-info',
      content: 'Qui trovi le informazioni del collega con cui stai collaborando.',
      disableBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-chat-messages',
      content: 'La conversazione con il collega appare qui. I tuoi messaggi sono a destra, quelli del collega a sinistra.',
      placement: 'top' as const
    },
    ...(!isClosed ? [
      {
        target: '#tour-chat-reply',
        content: 'Scrivi qui il tuo messaggio e premi Invio o il bottone per inviarlo.',
        placement: 'top' as const
      },
      {
        target: '#tour-chat-close',
        content: 'Quando la collaborazione è conclusa, puoi chiuderla da qui. La conversazione resterà consultabile.',
        placement: 'bottom' as const
      }
    ] : [])
  ];

  const handleChatTourEvent = (data: any) => {
    if (data.type === 'tour:end' || data.action === 'skip' || data.action === 'close') {
      setRunTourChat(false);
      localStorage.setItem(TOUR_CHAT_KEY, 'true');
    }
  };

  // Load current associate
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/accesso-associati');
      return;
    }
    const data = getAssociateData();
    if (data?.id) setCurrentAssociateId(data.id);
  }, [navigate]);

  // Load collaboration detail
  const loadDetail = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/collaborations/my/${id}`, {
        headers: getAuthHeader()
      });
      if (data.success) {
        setCollaboration(data.data);
        // Tour alla prima visita
        if (!localStorage.getItem(TOUR_CHAT_KEY)) {
          setTimeout(() => setRunTourChat(true), 600);
        }
      }
    } catch {
      toast.error('Collaborazione non trovata');
      navigate('/area-riservata/collabora');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    if (id) loadDetail();
  }, [id, loadDetail]);

  // Socket.IO real-time
  useEffect(() => {
    if (!id || !currentAssociateId) return;

    const token = getToken();
    if (!token) return;
    // Il socket /notifications è aperto anche al dipendente (con `collabora`), ma questa pagina confronta mittente
    // e «sta scrivendo» con l'id dello studio: per il dipendente va adeguata per persona in un passo successivo.
    // Fino ad allora il dipendente aggiorna col REST.
    if (sessioneDiDipendente()) return;

    const socket = io(`${SOCKET_URL}/notifications`, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_collaboration', { collaborationId: id });
    });

    socket.on('collaboration_new_message', (data: { collaborationId: string; message: ConversationMsg }) => {
      if (data.collaborationId === id && data.message.senderAssociateId !== currentAssociateId) {
        setCollaboration(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            conversation: [...prev.conversation, data.message]
          };
        });
      }
    });

    // Typing indicator
    socket.on('collaboration_typing', (data: { collaborationId: string; associateId: string; typing: boolean }) => {
      if (data.collaborationId === id && data.associateId !== currentAssociateId) {
        setOtherTyping(data.typing);
      }
    });

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      socket.emit('leave_collaboration', { collaborationId: id });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [id, currentAssociateId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [collaboration?.conversation?.length]);

  // Send reply
  const handleSendReply = async () => {
    if ((!reply.trim() && selectedFiles.length === 0) || sending) return;

    // Stop typing indicator
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socketRef.current?.emit('collaboration_typing_stop', { collaborationId: id });

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', reply.trim() || (selectedFiles.length > 0 ? `${selectedFiles.length} allegato/i` : ''));
      selectedFiles.forEach(f => formData.append('attachments', f));

      const headers = getAuthHeader() as Record<string, string>;

      const { data } = await axios.post(`${API_BASE}/collaborations/my/${id}/reply`, formData, {
        headers
      });

      if (data.success) {
        setCollaboration(data.data);
        setReply('');
        setSelectedFiles([]);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Errore durante l\'invio');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const invalid = files.find(f => f.size > maxSize);
    if (invalid) {
      toast.error(`${invalid.name} supera i 10MB`);
      return;
    }
    setSelectedFiles(prev => [...prev, ...files].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Close collaboration (dopo conferma modale)
  const handleConfirmClose = async () => {
    setShowCloseConfirm(false);
    try {
      const { data } = await axios.patch(`${API_BASE}/collaborations/my/${id}/close`, {}, {
        headers: getAuthHeader()
      });
      if (data.success) {
        setCollaboration(data.data);
        toast.success('Collaborazione chiusa');
      }
    } catch {
      toast.error('Errore durante la chiusura');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!collaboration) return null;

  const isRequester = collaboration.requesterId === currentAssociateId;
  const otherParty = isRequester ? collaboration.targetSnapshot : collaboration.requesterSnapshot;
  const statusCfg = STATUS_CONFIG[collaboration.status] || STATUS_CONFIG.new;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      {/* Guided Tour */}
      <Joyride
        key={`chat-tour-${tourChatKey}`}
        steps={chatTourSteps}
        run={runTourChat}
        continuous
        scrollToFirstStep
        onEvent={handleChatTourEvent}
        locale={{ back: 'Indietro', close: 'Chiudi', last: 'Fine', next: 'Avanti', skip: 'Non mostrare più' }}
        options={{
          primaryColor: '#16a34a',
          zIndex: 10000,
          buttons: ['back', 'close', 'primary'],
          showProgress: true,
          scrollOffset: 80,
          textColor: '#374151',
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          width: 340
        }}
        styles={{
          tooltip: { borderRadius: 12, padding: 20, fontFamily: 'inherit', fontSize: 13 }
        } as any}
      />

      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/area-riservata/collabora')} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow">
            <Handshake className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-base font-bold text-gray-900 dark:text-white">Collaborazione</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-amber-600 dark:text-amber-400">{PRACTICE_ICONS[collaboration.practiceType] || '📋'} {collaboration.practiceType}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
            </div>
          </div>
          <button
            onClick={() => { setTourChatKey(k => k + 1); setRunTourChat(true); }}
            className="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors"
            title="Guida"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Info controparte */}
        <div id="tour-chat-info" className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">
            {isRequester ? 'Collega contattato' : 'Richiesta da'}
          </p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">{otherParty?.nominativo || 'N/D'}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" /> {otherParty?.citta || ''} {otherParty?.provincia ? `(${otherParty.provincia})` : ''}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" /> Avviata il {new Date(collaboration.createdAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Conversazione */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div id="tour-chat-messages" className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Conversazione</span>
              <span className="text-xs text-gray-400">({collaboration.conversation.length})</span>
            </div>
            {!isClosed && (
              <button id="tour-chat-close" onClick={() => setShowCloseConfirm(true)} className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Chiudi
              </button>
            )}
          </div>

          {/* Messaggi */}
          <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
            {collaboration.conversation.map((msg, i) => {
              const isMine = msg.senderAssociateId === currentAssociateId;
              return (
                <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${isMine ? 'order-2' : ''}`}>
                    <div className={`rounded-2xl px-4 py-3 ${
                      isMine
                        ? 'bg-amber-500 text-white rounded-br-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                      {/* Allegati */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {msg.attachments.map((att, j) => {
                            const isImage = att.mimetype?.startsWith('image/');
                            const fileUrl = `${API_BASE.replace('/api', '')}${att.url}`;
                            return isImage ? (
                              <a key={j} href={fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                                <img src={fileUrl} alt={att.originalName} className="max-w-[240px] rounded-lg border border-white/20" />
                              </a>
                            ) : (
                              <a key={j} href={fileUrl} target="_blank" rel="noopener noreferrer"
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                                  isMine ? 'bg-amber-600/50 text-white hover:bg-amber-600/70' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                                } transition-colors`}
                              >
                                <FileText className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{att.originalName}</span>
                                <Download className="w-3 h-3 flex-shrink-0 ml-auto" />
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{msg.senderName}</span>
                      <span className="text-xs text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(msg.sentAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMine && msg.readAt && (
                        <CheckCircle className="w-3 h-3 text-green-500 ml-0.5" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Typing indicator */}
            {otherTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input risposta */}
          {!isClosed ? (
            <div id="tour-chat-reply" className="p-4 border-t border-gray-200 dark:border-gray-700">
              {/* Preview file selezionati */}
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                      {f.type.startsWith('image/') ? <Image className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                      <span className="max-w-[120px] truncate">{f.name}</span>
                      <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="self-end p-2 text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded-xl transition-colors"
                  title="Allega file (PDF, JPG, PNG)"
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                <textarea
                  value={reply}
                  onChange={e => {
                    setReply(e.target.value);
                    if (socketRef.current?.connected && id) {
                      socketRef.current.emit('collaboration_typing_start', { collaborationId: id });
                      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                      typingTimeoutRef.current = setTimeout(() => {
                        socketRef.current?.emit('collaboration_typing_stop', { collaborationId: id });
                      }, 2000);
                    }
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                  placeholder="Scrivi un messaggio..."
                  rows={2}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
                <button
                  onClick={handleSendReply}
                  disabled={(!reply.trim() && selectedFiles.length === 0) || sending}
                  className="self-end px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, WebP — max 10MB, max 5 file</p>
            </div>
          ) : (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">Questa collaborazione è stata chiusa.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modale conferma chiusura */}
      <ConfirmDialog
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleConfirmClose}
        title="Chiudi collaborazione"
        message="Chiudendo la collaborazione non sarà più possibile scambiare messaggi con il collega. La conversazione resterà consultabile ma non modificabile. Vuoi procedere?"
        confirmText="Chiudi collaborazione"
        cancelText="Annulla"
        type="warning"
      />
    </div>
  );
}
